/**
 * A money figure to the cent, rounded half away from zero on the DECIMAL value - the way a servicer
 * states a figure, the way The Mortgage Office quotes one, and the convention loans are billed on.
 * The port of the server's `utils/round-cents`, so a figure this app previews is the figure the
 * server will store rather than one a cent under it.
 *
 * `Number(n.toFixed(2))` rounds the binary double instead, and almost no decimal that ends in half
 * a cent has an exact double. Daily interest lands on one constantly: a balance times a rate over
 * a day count is a third, a sixth or a ninth of a cent far more often than it is even. $165,000 at
 * 10.49% on an actual/360 basis for 18 days is exactly $865.425, whose nearest double is
 * 865.42499999999995 - below the midpoint - so `toFixed` quotes $865.42 while the prior servicer
 * quotes $865.43.
 *
 * So the digits are read off `toPrecision(15)`, the most significant digits at which every decimal
 * still maps to a distinct double: drift at the 16th and 17th digit is absorbed, while a genuine
 * near-miss keeps its own digits and stays down.
 *
 * Non-finite input is passed straight back, NaN included: a figure that is not a number is a bug
 * upstream, and quietly turning it into $0 hides it.
 */
export const roundToCents = (value: string | number): number => {
  const n = Number(value)
  if (!Number.isFinite(n)) return n

  const abs = Math.abs(n)
  // `toPrecision` pads the fraction out to 15 significant digits, so any figure under 1e15 has a
  // decimal point with digits past it; at or above that it switches to exponential notation, as it
  // does below a ten-thousandth of a cent. Neither extreme has a half cent to resolve.
  const text = abs.toPrecision(15)
  if (text.includes('e')) return abs < 1 ? (n < 0 ? -0 : 0) : n

  const dot = text.indexOf('.')
  // No point at all is 15 digits of whole dollars, past where a double can hold cents anyway.
  if (dot < 0) return n
  const fraction = text.slice(dot + 1)
  // Already quoted to the cent or better, the 15 digits having run out before the third decimal.
  if (fraction.length <= 2) return n

  // Cents read straight off the digits, so nothing is divided before the rounding decision: the
  // whole part and the first two decimals concatenate into the integer number of cents.
  const cents = Number(text.slice(0, dot) + fraction.slice(0, 2))
  const rounded = (fraction[2] >= '5' ? cents + 1 : cents) / 100
  return n < 0 ? -rounded : rounded
}
